import React, { useEffect, useState } from "react";
import { useSalones } from "../context/SalonesContext";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import HeaderPage from "../components/HeaderPage";
import DropDown from "../components/DropDown";
import { useUsers } from "../context/UsersContext";
import { FiDownload } from "react-icons/fi";

const HorariosPage = () => {
  const { horarios, uploadHorarioData, fetchHorario, loading } = useSalones();
  const { salones, fetchSalones } = useUsers();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedSalon, setSelectedSalon] = useState(""); // Salón seleccionado por el rector
  const [salonInfo, setSalonInfo] = useState({ nombre: "", grado: "" });

  useEffect(() => {
    if (user.rol_id === 1) {
      fetchSalones(); // Cargar lista de salones si es rector
    } else {
      const loadHorario = async () => {
        const salonId =
          user?.rol_id === 3 ? user.salon_id : user.director_salon;

        if (salonId) {
          await fetchHorario(salonId);

          // Actualizar información del salón basado en el horario
          if (horarios.length > 0) {
            setSalonInfo({
              nombre: horarios[0].nombre || "",
              grado: horarios[0].grado || "",
            });
          }
        }
      };

      loadHorario();
    }
  }, [fetchHorario, fetchSalones, user, horarios]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target.result); // Previsualizar la imagen
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (file) {
      const formData = new FormData();
      formData.append("horario_image", file);
      formData.append("salon_id", user.director_salon);

      await uploadHorarioData(formData);
      setFile(null);
      setPreview(null);
      setShowModal(false);
    }
  };

  const closeModal = () => {
    setShowModal(false); // Cerrar el modal
    setFile(null);
    setPreview(null);
  };

  const handleSalonChange = async (e) => {
    const salonId = e.target.value;
    setSelectedSalon(salonId);

    if (salonId) {
      await fetchHorario(salonId);
    }
  };

  // Función para forzar la descarga de la imagen
  const handleDownload = async () => {
    try {
      const imageUrl = horarios[0].horario_image;
      const response = await fetch(imageUrl, {
        mode: "cors",
      });

      if (!response.ok) throw new Error("Error al descargar el horario");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = imageUrl.split("/").pop() || "horario.jpg";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-6 overflow-y-auto">
        <HeaderPage title="Gestión de Horarios" />

        {/* Filtro para el rector */}
        {user.rol_id === 1 && (
          <div className="mb-6">
            <label
              htmlFor="salon-select"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Seleccione un salón:
            </label>
            <DropDown
              value={selectedSalon}
              onChange={handleSalonChange}
              placeholder="Seleccione un salón"
              options={salones.map((salon) => ({
                value: salon.id,
                label: `${salon.nombre} - ${salon.grado}`
              }))}
            />
          </div>
        )}

        {/* Información del salón solo para profesores y estudiantes */}
        {(user.rol_id === 2 || user.rol_id === 3) &&
          salonInfo.nombre &&
          salonInfo.grado && (
            <div className="mb-4 text-center">
              <h2 className="text-xl font-semibold">
                {salonInfo.nombre} - {salonInfo.grado}
              </h2>
            </div>
          )}

        {user.rol_id === 3 ? (
          // Vista para Estudiantes
          <div>
            {loading ? (
              <p className="text-center">Cargando...</p>
            ) : horarios.length > 0 ? (
              <div className="flex justify-center">
                <img
                  src={horarios[0].horario_image}
                  alt="Horario"
                  className="max-w-full max-h-[76vh] rounded-lg shadow-md"
                />
              </div>
            ) : (
              <p className="text-center">
                No hay horarios disponibles para este salón.
              </p>
            )}
          </div>
        ) : user.director_salon ? (
          // Vista para Directores
          <div>
            <div className="mb-6 text-center">
              <button
                onClick={() => setShowModal(true)} // Abre el modal
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Subir Horario
              </button>
            </div>
            {loading ? (
              <p className="text-center">Cargando...</p>
            ) : horarios.length > 0 ? (
              <div className="flex justify-center">
                <img
                  src={horarios[0].horario_image}
                  alt="Horario"
                  className="max-w-full max-h-[76vh] rounded-lg shadow-md"
                />
              </div>
            ) : (
              <p className="text-center">
                No hay horarios disponibles para este salón.
              </p>
            )}
          </div>
        ) : user.rol_id === 1 ? (
          // Vista para Rector con filtro
          <div>
            {loading ? (
              <p className="text-center">Cargando...</p>
            ) : horarios.length > 0 ? (
              <div className="flex justify-center">
                <img
                  src={horarios[0].horario_image}
                  alt="Horario"
                  className="max-w-full max-h-[76vh] rounded-lg shadow-md"
                />
              </div>
            ) : (
              <p className="text-center">
                Seleccione un salón para ver el horario.
              </p>
            )}
          </div>
        ) : (
          <p className="text-center">
            No tienes asignado un salón como director.
          </p>
        )}

        {horarios.length > 0 && (
          <div className="flex flex-col items-center">
            {loading ? (
              <p className="text-center">Cargando...</p>
            ) : (
              <div className="flex flex-col items-center">
                <button
                  onClick={handleDownload}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-800 transition flex items-center"
                >
                  <FiDownload className="mr-2" />
                  Descargar Horario
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modal para subir imagen */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
              <h2 className="text-xl font-semibold mb-4">Subir Horario</h2>
              <input
                type="file"
                onChange={handleFileChange}
                className="mb-4 block w-full border border-gray-300 p-2 rounded"
              />
              {preview && (
                <div className="mb-4">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-w-full max-h-64 rounded-lg shadow-md"
                  />
                </div>
              )}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={closeModal} // Cerrar el modal al hacer clic en Cancelar
                  className="bg-gray text-white px-4 py-2 rounded"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpload} // Subir la imagen
                  className="bg-green text-white px-4 py-2 rounded"
                >
                  Subir Imagen
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default HorariosPage;
